@if (loading()) {
  <section class="ingest-upload">
    <div class="loading-container">
      <lib-empty message="Cargando datos del expediente" description="Espere por favor..." />
    </div>
  </section>
}

@if (!loading() && error) {
  <section class="ingest-upload">
    <p class="ingest-upload__error">{{ error }}</p>
    <lib-button variant="secondary" type="button" (clicked)="goBackToDetail()">
      Volver al detalle
    </lib-button>
  </section>
}

@if (!loading() && !error && project; as p) {
  <section class="ingest-upload">
    <app-page-header
      [title]="'Ingestar documentos · ' + p.name"
      subtitle="Sube archivos sueltos y/o carpetas con estructura; revisa qué se enviará, el progreso y el resultado."
      icon="Upload"
      [actions]="headerActions()"
      (actionClick)="onHeaderAction($event)"
      />
    @if (subfoldersWarning(); as warning) {
      <p class="ingest-upload__notice" role="status">{{ warning }}</p>
    }
    <div class="ingest-upload__form">
      @for (_ of [metaFormResetKey()]; track _) {
      <app-grid-form preset="responsive" wrapperClass="ingest-upload__meta-grid">
        <app-grid-form-item [span]="2" class="ingest-field">
          <app-field-label
            label="Empresa"
            [required]="true"
            tooltip="Nombre de la entidad responsable de esta ingesta."
            />
          <lib-input
            label=""
            placeholder="Ej: ACME"
            [value]="companyName"
            (valueChange)="onCompanyNameChange($event)"
            />
        </app-grid-form-item>
        <app-grid-form-item [span]="2" class="ingest-field">
          <app-field-label
            label="Subcarpeta destino"
            tooltip="Carpeta dentro del expediente donde quedarán organizados los archivos."
            />
          <lib-select
            label=""
            placeholder="(Raíz)"
            [options]="subfolderOptions()"
            [value]="subfolderSelectValue"
            (valueChange)="onSubfolderPathChange($event)"
            [searchable]="true"
            hint="Elige una carpeta existente o deja la raíz."
            />
        </app-grid-form-item>
        <app-grid-form-item [span]="2" class="ingest-field">
          <app-field-label
            label="Descripción (opcional)"
            tooltip="Comentario de referencia para identificar esta carga."
            />
          <lib-input
            label=""
            placeholder="Descripción de la ingesta"
            [value]="description"
            (valueChange)="onDescriptionChange($event)"
            />
        </app-grid-form-item>
      </app-grid-form>
      }
      @if (uploadLines().length > 0) {
        <div class="ingest-upload__progress-panel">
          <app-upload-progress-tracker [items]="uploadLines()" />
        </div>
      }

      <!-- Tipo detectado (solo lectura): se actualiza solo al cargar archivos -->
      <div class="ingest-upload__mode">
        <span class="ingest-upload__mode-label">Tipo detectado</span>
        <mat-button-toggle-group [value]="uploadMode()" [disabled]="true" aria-label="Tipo de carga detectado">
          <mat-button-toggle value="documento">Documento</mat-button-toggle>
          <mat-button-toggle value="expediente">Expediente</mat-button-toggle>
        </mat-button-toggle-group>
        <p class="ingest-upload__mode-hint">
          @if (uploadMode() === 'expediente') {
            Expediente ISO detectado (1 .iso + 5 sidecars): carga resumable por chunks, verificación de
            integridad MD5/SHA1 y publicación atómica.
          } @else {
            Documento(s): subida estándar. Se activa «Expediente» al seleccionar 1 .iso + sus 5 sidecars.
          }
        </p>
      </div>

      <div class="ingest-upload__sources">
        <div class="ingest-upload__source-card">
          <div class="ingest-upload__source-header">
            <div>
              <h3 class="ingest-upload__source-title">Archivos sueltos</h3>
              <p class="ingest-upload__hint">
                Selección múltiple; cada nueva selección sustituye solo los archivos sueltos preparados para
                el envío (no modifica las carpetas ya añadidas).
              </p>
            </div>
            <div class="ingest-upload__source-actions">
              <lib-button
                variant="secondary"
                type="button"
                [disabled]="looseCount === 0 || uploading()"
                (clicked)="clearLooseFiles()"
                >
                Quitar archivos sueltos
              </lib-button>
              @if (looseCount > 0) {
                <span class="ingest-upload__badge">{{ looseCount }} archivo(s)</span>
              }
            </div>
          </div>
          @for (n of [filePickerReset()]; track n) {
            <lib-file-upload
              label="Seleccionar archivos"
            [hint]="
              'Tipos (archivos sueltos): ' +
              looseFilesHintTypes +
              '. Máx. ' +
              maxFileSizeLabel +
              ' por archivo.'
            "
              [accept]="acceptExtensions"
              [multiple]="true"
              [maxSize]="maxFileBytes"
              [disabled]="uploading()"
              (filesChanged)="onFilesChanged($event)"
              />
          }
        </div>
        <div class="ingest-upload__source-card">
          <div class="ingest-upload__source-header">
            <div>
              <h3 class="ingest-upload__source-title">Carpetas (estructura)</h3>
              <p class="ingest-upload__hint">
                Elige una carpeta del equipo: se incluyen todos los archivos y subcarpetas. Puedes pulsar
                «Añadir carpeta» varias veces para sumar más carpetas al mismo envío.
                {{ ' ' + folderFilesHint }}
              </p>
            </div>
            <div class="ingest-upload__source-actions">
              <lib-button
                variant="primary"
                type="button"
                prefixIcon="FolderUp"
                [disabled]="uploading()"
                (clicked)="onAddFolderClick(folderInput)"
                >
                Añadir carpeta
              </lib-button>
              <lib-button
                variant="secondary"
                type="button"
                [disabled]="folderQueueCount === 0 || uploading()"
                (clicked)="clearFolderQueue()"
                >
                Quitar carpetas
              </lib-button>
              @if (folderQueueCount > 0) {
                <span class="ingest-upload__badge">
                  {{ folderQueueCount }} archivo(s) desde carpetas
                </span>
              }
            </div>
          </div>
          <input
            #folderInput
            type="file"
            class="ingest-upload__folder-input"
            multiple
            webkitdirectory
            [accept]="acceptExtensions"
            (change)="onFolderInputChange($event)"
            />
        </div>
      </div>
      @if (totalQueued > 0) {
        <div class="ingest-upload__queue-summary">
          <span><strong>Ingesta total:</strong> {{ totalQueued }} archivo(s)</span>
          <lib-button variant="secondary" type="button" [disabled]="uploading()" (clicked)="clearAllQueued()">
            Limpiar ingesta
          </lib-button>
        </div>
      }
    </div>
  </section>
}

@if (uploadResultDialog(); as d) {
  <div class="ingest-upload__modal-backdrop" role="presentation">
    <div
      class="ingest-upload__modal"
      [class.ingest-upload__modal--error]="d.kind === 'error'"
      role="dialog"
      [attr.aria-modal]="true"
      [attr.aria-labelledby]="'ingest-upload-result-title'"
    >
      <h2 class="ingest-upload__modal-title" id="ingest-upload-result-title">
        @if (d.kind === 'success') {
          {{ d.headline }}
        } @else {
          Error en la carga
        }
      </h2>
      @if (d.kind === 'success' && d.summary) {
        @if (d.intro) {
          <p class="ingest-upload__modal-intro">{{ d.intro }}</p>
        }
        <div class="ingest-upload__modal-details">
          <p><strong>Estado:</strong> {{ d.summary.status }}</p>
          <p><strong>Upload ID:</strong> {{ d.summary.upload_id }}</p>
          <p><strong>Mensaje:</strong> {{ d.summary.message }}</p>
          @if (d.summary.files.length) {
            <p>
              <strong>Archivos:</strong>
              {{ d.summary.files.join(', ') }}
            </p>
          }
        </div>
      } @else if (d.kind === 'success' && d.intro) {
        <p class="ingest-upload__modal-intro">{{ d.intro }}</p>
        <p class="ingest-upload__modal-message">{{ d.headline }}</p>
      } @else {
        <p class="ingest-upload__modal-message">{{ d.headline }}</p>
      }
      <div class="ingest-upload__modal-actions">
        <lib-button variant="primary" type="button" (clicked)="closeUploadResultDialog()">
          Aceptar
        </lib-button>
        @if (uploadMode() === 'expediente' && d.kind === 'success') {
          <lib-button
            variant="secondary"
            type="button"
            (clicked)="closeUploadResultDialog(); verRecorrido()"
          >
            Ver recorrido
          </lib-button>
        }
        <lib-button variant="secondary" type="button" (clicked)="closeUploadResultDialog()">
          Cerrar
        </lib-button>
      </div>
    </div>
  </div>
}
